<template>
  <div class="space-y-6">
    <!-- Header de la pestaña de Ventas -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div>
        <h2 class="text-base font-bold text-gray-900 flex items-center gap-2">
          <i class="fa fa-chart-line text-orange-500" />
          <span>Informe Detallado de Ventas</span>
        </h2>
        <p class="text-xs text-gray-500">Desempeño comercial por producto, vendedor y canales de cobro.</p>
      </div>

      <div class="flex items-center gap-2">
        <a
          :href="getPdfUrl('sales')"
          target="_blank"
          class="px-3.5 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
        >
          <i class="fa fa-file-pdf text-rose-600" />
          <span>Exportar Ventas PDF</span>
        </a>
      </div>
    </div>

    <!-- Tabla: Top 15 Productos Más Vendidos -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i class="fa fa-trophy text-amber-500" />
          <h3 class="text-sm font-bold text-gray-900">Top Productos Más Vendidos</h3>
        </div>
        <span class="text-xs text-gray-400 font-medium">
          Ordenado por ingresos totales
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px] border-b border-gray-100">
            <tr>
              <th class="py-3 px-4 text-center" style="width: 5%;">#</th>
              <th class="py-3 px-4" style="width: 35%;">Producto</th>
              <th class="py-3 px-3">Categoría</th>
              <th class="py-3 px-3 text-right">Unid. Vendidas</th>
              <th class="py-3 px-3 text-right">Total Ventas ($)</th>
              <th class="py-3 px-3 text-right">Costo Total ($)</th>
              <th class="py-3 px-3 text-right">Ganancia ($)</th>
              <th class="py-3 px-3 text-center">Margen</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(prod, idx) in sales.top_products"
              :key="prod.id"
              class="hover:bg-orange-50/20 transition-colors"
            >
              <td class="py-3 px-4 text-center font-bold text-gray-400">
                <span
                  class="w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px]"
                  :class="idx < 3 ? 'bg-amber-100 text-amber-800 font-black' : 'bg-gray-100 text-gray-600'"
                >
                  {{ idx + 1 }}
                </span>
              </td>
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ prod.name }}</div>
                <div class="text-[10px] text-gray-400 font-mono">{{ prod.code }}</div>
              </td>
              <td class="py-3 px-3">
                <span class="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700">
                  {{ prod.category }}
                </span>
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-gray-900">
                {{ formatNumber(prod.units_sold) }}
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-orange-600">
                {{ formatCurrency(prod.total_revenue) }}
              </td>
              <td class="py-3 px-3 text-right font-mono text-gray-500">
                {{ formatCurrency(prod.total_cogs) }}
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-emerald-600">
                {{ formatCurrency(prod.profit) }}
              </td>
              <td class="py-3 px-3 text-center">
                <span
                  class="px-2 py-0.5 rounded-full text-[10px] font-bold"
                  :class="prod.margin_pct >= 30 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'"
                >
                  {{ prod.margin_pct }}%
                </span>
              </td>
            </tr>
            <tr v-if="!sales.top_products?.length">
              <td colspan="8" class="text-center py-8 text-gray-400">
                <i class="fa fa-shopping-cart text-2xl text-gray-300 mb-2 block" />
                <span>No se registraron ventas en el rango seleccionado.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Fila 2: Ventas por Vendedor y Métodos de Pago -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Ventas por Vendedor -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div class="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
          <i class="fa fa-user-tie text-blue-500" />
          <div>
            <h3 class="text-sm font-bold text-gray-900">Rendimiento por Vendedor</h3>
            <p class="text-[11px] text-gray-400">Volumen de ventas y efectividad de cobro</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left">
            <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px]">
              <tr>
                <th class="py-2.5 px-3">Vendedor</th>
                <th class="py-2.5 px-2 text-center">Órdenes</th>
                <th class="py-2.5 px-2 text-right">Total ($)</th>
                <th class="py-2.5 px-2 text-right">Cobrado ($)</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="seller in sales.by_seller"
                :key="seller.responsible_id || seller.name"
                class="hover:bg-blue-50/20"
              >
                <td class="py-2.5 px-3 font-bold text-gray-900">
                  {{ seller.name }}
                </td>
                <td class="py-2.5 px-2 text-center font-mono">
                  {{ seller.orders_count }}
                </td>
                <td class="py-2.5 px-2 text-right font-mono font-bold text-gray-900">
                  {{ formatCurrency(seller.total) }}
                </td>
                <td class="py-2.5 px-2 text-right font-mono font-semibold text-emerald-600">
                  {{ formatCurrency(seller.collected) }}
                </td>
              </tr>
              <tr v-if="!sales.by_seller?.length">
                <td colspan="4" class="text-center py-4 text-gray-400">Sin datos de vendedores</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Ventas por Método de Pago -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div class="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
          <i class="fa fa-money-bill-wave text-emerald-500" />
          <div>
            <h3 class="text-sm font-bold text-gray-900">Cobros por Método de Pago</h3>
            <p class="text-[11px] text-gray-400">Distribución de ingresos recaudados</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left">
            <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px]">
              <tr>
                <th class="py-2.5 px-3">Método</th>
                <th class="py-2.5 px-2 text-center">Transacciones</th>
                <th class="py-2.5 px-2 text-right">Total USD</th>
                <th class="py-2.5 px-2 text-right">Total Bs</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="pm in sales.by_payment_method"
                :key="pm.method"
                class="hover:bg-emerald-50/20"
              >
                <td class="py-2.5 px-3 font-bold text-gray-800 uppercase">
                  {{ pm.method }}
                </td>
                <td class="py-2.5 px-2 text-center font-mono">
                  {{ pm.count }}
                </td>
                <td class="py-2.5 px-2 text-right font-mono font-bold text-emerald-600">
                  {{ formatCurrency(pm.total_usd) }}
                </td>
                <td class="py-2.5 px-2 text-right font-mono text-gray-500">
                  Bs {{ formatNumber(pm.total_bs) }}
                </td>
              </tr>
              <tr v-if="!sales.by_payment_method?.length">
                <td colspan="4" class="text-center py-4 text-gray-400">Sin pagos registrados</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SalesTab',

  props: {
    sales: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },

  methods: {
    getPdfUrl(type) {
      const from = this.filters.date_from || ''
      const to = this.filters.date_to || ''
      return `/reports/export/pdf?type=${type}&date_from=${from}&date_to=${to}`
    },

    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(val || 0)
    },

    formatNumber(val) {
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(val || 0)
    },
  },
}
</script>
